import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  faEdit,
  faTrash,
  faEye,
  faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Page from '@/components/Page';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Chip from '@/components/Chip';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import { PostsAPI } from '@/services/postsAPI';
import { Post } from '@/types/posts';
import DeleteConfirmModal from './DeleteConfirmModal';
import ROUTES from '@/routes/routes';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'update', label: 'Update' },
  { value: 'tip', label: 'Tip' },
  { value: 'news', label: 'News' },
];

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch (e) {
    return dateString;
  }
};

const getStatusChip = (status: string) => {
  switch (status) {
    case 'published':
      return <Chip color="success" label="Published" size="small" />;
    case 'draft':
      return <Chip color="warning" label="Draft" size="small" />;
    case 'archived':
      return <Chip label="Archived" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
};

const Posts: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<Post[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  useEffect(() => {
    fetchPosts();
  }, [statusFilter, categoryFilter, searchInput, currentPage]);

  const fetchPosts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await PostsAPI.getPosts(
        {
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          search: searchInput || undefined,
        },
        { page: currentPage, per_page: perPage },
      );

      if (response.success) {
        setPosts(response.data);
        setTotalPages(response.pagination.total_pages);
        setTotalCount(response.pagination.total);
      }
    } catch (err: any) {
      console.error('Error fetching posts:', err);
      setError(err.response?.data?.errors || 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    navigate(`/${ROUTES.posts}/${ROUTES.postCreate}`);
  };

  const handleEditClick = (post: Post) => {
    navigate(`/${ROUTES.posts}/${post.id}/edit`);
  };

  const handleDeleteClick = (post: Post) => {
    setSelectedPost(post);
    setShowDeleteModal(true);
  };

  const handlePublishToggle = async (post: Post) => {
    try {
      if (post.status === 'published') {
        await PostsAPI.unpublishPost(post.id);
        setSuccess('Post unpublished successfully');
      } else {
        await PostsAPI.publishPost(post.id);
        setSuccess('Post published successfully');
      }
      fetchPosts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.errors || 'Failed to update post status');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeleteSuccess = () => {
    setShowDeleteModal(false);
    setSuccess('Post deleted successfully');
    fetchPosts();
    setTimeout(() => setSuccess(null), 3000);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setSearchInput('');
    setCurrentPage(1);
  };

  if (loading && posts.length === 0) {
    return (
      <Page title="Posts Management">
        <Card>
          <LoadingState />
        </Card>
      </Page>
    );
  }

  return (
    <Page
      primaryAction={{
        a11yLabel: 'Create a new post',
        label: 'New Post',
        onAction: handleCreateClick,
      }}
      size="full"
      title="Posts Management"
    >
      {error && (
        <Alert
          description={error}
          status="danger"
          title="Error"
          onDismiss={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          description={success}
          status="success"
          title="Success"
          onDismiss={() => setSuccess(null)}
        />
      )}

      <Card>
        <Card.Section>
          <Stack gap={12} vertical>
            <Stack gap={12}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Search by title..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={handleStatusChange}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={categoryFilter}
                  onChange={handleCategoryChange}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {(statusFilter || categoryFilter || searchInput) && (
                <Button
                  a11yLabel="Clear filters"
                  label="Clear"
                  onAction={handleClearFilters}
                />
              )}
            </Stack>
            <Small>
              Showing {posts.length} of {totalCount} posts
            </Small>
          </Stack>
        </Card.Section>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>
                  Title (EN)
                </th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Order</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>
                  Published
                </th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Expires</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{ padding: '24px', textAlign: 'center' }}
                  >
                    <Body>
                      No posts found. Create your first post to get started!
                    </Body>
                  </td>
                </tr>
              ) : (
                posts.map((post) => (
                  <tr key={post.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>
                      <Stack gap={4} vertical>
                        <Body weight="medium">{post.title_en}</Body>
                        <Small>{post.excerpt_en.substring(0, 60)}...</Small>
                      </Stack>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Small>{post.category}</Small>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {getStatusChip(post.status)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Small>{post.display_order}</Small>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Small>{formatDate(post.published_at)}</Small>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Small>{formatDate(post.expires_at)}</Small>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <Stack gap={8} justify="end">
                        <button
                          onClick={() => handlePublishToggle(post)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                          }}
                          title={
                            post.status === 'published'
                              ? 'Unpublish'
                              : 'Publish'
                          }
                        >
                          <FontAwesomeIcon
                            icon={
                              post.status === 'published' ? faEyeSlash : faEye
                            }
                            style={{ fontSize: '16px', color: '#666' }}
                          />
                        </button>
                        <button
                          onClick={() => handleEditClick(post)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                          }}
                          title="Edit"
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            style={{ fontSize: '16px', color: '#666' }}
                          />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(post)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                          }}
                          title="Delete"
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ fontSize: '16px', color: '#d32f2f' }}
                          />
                        </button>
                      </Stack>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Card.Section>
            <Stack justify="center">
              <Stack gap={8}>
                <Button
                  a11yLabel="Previous page"
                  disabled={currentPage === 1}
                  label="Previous"
                  onAction={() => handlePageChange(currentPage - 1)}
                />
                <Body>
                  Page {currentPage} of {totalPages}
                </Body>
                <Button
                  a11yLabel="Next page"
                  disabled={currentPage === totalPages}
                  label="Next"
                  onAction={() => handlePageChange(currentPage + 1)}
                />
              </Stack>
            </Stack>
          </Card.Section>
        )}
      </Card>

      {showDeleteModal && selectedPost && (
        <DeleteConfirmModal
          post={selectedPost}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </Page>
  );
};

export default Posts;
